import * as React from 'react';
import type { MessageKey } from '@reos/i18n';
import { useT } from '../lib/i18n.js';
import { baseUrl } from '../lib/api.js';
import { errorMessage } from '../lib/properties.js';

/**
 * `/interni/provoz` (krok D6b body 2 a 3) — PREHLED PROVOZOVATELE:
 *  - naklad a marze Kompletu nad REALNYMI zakazkami (prumer/median/max),
 *  - funnel hledani → precteny rozsah → demo → aktivace → zruseni.
 *
 * NENI TO OBRAZOVKA KANCELARE (zadani): naklad, marze a funnel jsou cisla
 * provozovatele. Proto stejny vzor jako `/interni/ai-superadmin` (ROZHODNUTI
 * KR1-2): zadny `requireAuth`, autorizace je sdilene tajemstvi v hlavicce
 * `x-superadmin-token`, token zije JEN v React state — nikdy v
 * localStorage/sessionStorage, takze obnovenim stranky zmizi.
 *
 * HODINOVA SAZBA je vstup prepoctu, ne ulozene nastaveni: R.B. si skutecne
 * cislo zkousí (viz Nalezy REPORT-step-D6.md — pri 400 Kc/h je kuponova cesta
 * ztratova) a server nic neuklada.
 *
 * KROKY FUNNELU MERENE BEZ COOKIES v Umami nemaji cislo (`count: null`) a
 * stranka u nich ukaze nazev udalosti. Radsi prazdno s odkazem, kde se to da
 * najit, nez cislo, ktere by si vymyslela.
 */

/**
 * Nazev kroku funnelu -> klic v katalogu. Mapa, ne skladani klice z hodnoty
 * ze serveru: klic s pomlckou by v katalogu nemel co delat a chybejici klic
 * shodi stranku (poucení S17i).
 */
const FUNNEL_LABEL_KEYS: Record<string, MessageKey> = {
  hledani: 'webAdmin.provoz.funnelStep.hledani' as MessageKey,
  'precteny-rozsah': 'webAdmin.provoz.funnelStep.prectenyRozsah' as MessageKey,
  demo: 'webAdmin.provoz.funnelStep.demo' as MessageKey,
  aktivace: 'webAdmin.provoz.funnelStep.aktivace' as MessageKey,
  zruseni: 'webAdmin.provoz.funnelStep.zruseni' as MessageKey,
};

interface CostOverview {
  readonly totalCostCzk: string;
  readonly marginVsListCzk: string;
  readonly marginVsMembershipCzk: string;
}

interface CostCase {
  readonly tenantId: string;
  readonly caseId: string;
  readonly humanMinutes: number;
  readonly overview: CostOverview;
}

interface CostReport {
  readonly summary: {
    readonly count: number;
    readonly avgTotalCostCzk: string;
    readonly medianTotalCostCzk: string;
    readonly maxTotalCostCzk: string;
    readonly avgMarginVsListCzk: string;
    readonly avgMarginVsMembershipCzk: string;
  } | null;
  readonly cases: readonly CostCase[];
  readonly hourlyRateCzk: string;
}

interface FunnelStep {
  readonly key: string;
  readonly source: 'db' | 'umami';
  readonly count: number | null;
  readonly umamiEvent: string | null;
}

interface Funnel {
  readonly steps: readonly FunnelStep[];
  readonly generatedAt: string;
}

async function adminFetch<T>(token: string, path: string): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`, { headers: { 'x-superadmin-token': token } });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

export function ProvozSuperadminPage(): React.JSX.Element {
  const t2 = useT();
  const [token, setToken] = React.useState('');
  const [activeToken, setActiveToken] = React.useState<string | null>(null);
  const [hourlyRate, setHourlyRate] = React.useState('400');
  const [costs, setCosts] = React.useState<CostReport | null>(null);
  const [funnel, setFunnel] = React.useState<Funnel | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async (tok: string, rate: string): Promise<void> => {
    setError(null);
    try {
      const [cost, fun] = await Promise.all([
        adminFetch<CostReport>(
          tok,
          `/api/internal/provoz/komplet-naklady?hourlyRateCzk=${encodeURIComponent(rate)}`,
        ),
        adminFetch<Funnel>(tok, '/api/internal/provoz/funnel'),
      ]);
      setCosts(cost);
      setFunnel(fun);
      setActiveToken(tok);
    } catch (e) {
      setError(errorMessage(e));
      setActiveToken(null);
    }
  }, []);

  if (activeToken === null) {
    return (
      <div className="p-6" data-testid="provoz-superadmin-login">
        <h1 className="mb-2 text-xl font-bold">{t2('webAdmin.provoz.title')}</h1>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void load(token, hourlyRate);
          }}
          className="flex max-w-md gap-2"
        >
          <input
            type="password"
            className="flex-1 rounded border px-2 py-1 text-sm"
            placeholder={t2('webAdmin.provoz.tokenLabel')}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid="provoz-superadmin-token"
          />
          <button type="submit" className="rh-a2-link text-sm" data-testid="provoz-superadmin-login-submit">
            {t2('webAdmin.provoz.tokenSubmit')}
          </button>
        </form>
        {error !== null ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="provoz-superadmin-page">
      <h1 className="mb-4 text-xl font-bold">{t2('webAdmin.provoz.title')}</h1>

      <h2 className="mb-1 text-sm font-semibold">{t2('webAdmin.provoz.costTitle')}</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void load(activeToken, hourlyRate);
        }}
        className="mb-3 flex items-end gap-2"
      >
        <input
          type="text"
          className="w-28 rounded border px-2 py-1 text-sm"
          value={hourlyRate}
          onChange={(e) => setHourlyRate(e.target.value)}
          placeholder={t2('webAdmin.provoz.hourlyRateLabel')}
          data-testid="provoz-superadmin-hourly-rate"
        />
        <button type="submit" className="rh-a2-link text-sm" data-testid="provoz-superadmin-reload">
          {t2('webAdmin.provoz.reload')}
        </button>
      </form>

      {costs?.summary === null || costs === null ? (
        <p className="mb-4 text-sm" data-testid="provoz-superadmin-costs-empty">
          {t2('webAdmin.provoz.costEmpty')}
        </p>
      ) : (
        <table className="mb-4 w-full text-sm" data-testid="provoz-superadmin-costs">
          <tbody>
            <tr>
              <td>{t2('webAdmin.provoz.costCount')}</td>
              <td data-testid="provoz-superadmin-cost-count">{String(costs.summary.count)}</td>
            </tr>
            <tr>
              <td>{t2('webAdmin.provoz.costAvg')}</td>
              <td data-testid="provoz-superadmin-cost-avg">{costs.summary.avgTotalCostCzk}</td>
            </tr>
            <tr>
              <td>{t2('webAdmin.provoz.costMedian')}</td>
              <td>{costs.summary.medianTotalCostCzk}</td>
            </tr>
            <tr>
              <td>{t2('webAdmin.provoz.costMax')}</td>
              <td>{costs.summary.maxTotalCostCzk}</td>
            </tr>
            <tr>
              <td>{t2('webAdmin.provoz.marginList')}</td>
              <td>{costs.summary.avgMarginVsListCzk}</td>
            </tr>
            <tr>
              <td>{t2('webAdmin.provoz.marginMembership')}</td>
              <td>{costs.summary.avgMarginVsMembershipCzk}</td>
            </tr>
            <tr>
              <td>{t2('webAdmin.provoz.minutesMeasured')}</td>
              <td data-testid="provoz-superadmin-minutes">
                {String(costs.cases.reduce((sum, item) => sum + item.humanMinutes, 0))}
              </td>
            </tr>
          </tbody>
        </table>
      )}

      <h2 className="mb-1 text-sm font-semibold">{t2('webAdmin.provoz.funnelTitle')}</h2>
      <table className="w-full text-sm" data-testid="provoz-superadmin-funnel">
        <tbody>
          {(funnel?.steps ?? []).map((step) => (
            <tr key={step.key} data-testid={`provoz-superadmin-funnel-${step.key}`}>
              <td>{FUNNEL_LABEL_KEYS[step.key] === undefined ? step.key : t2(FUNNEL_LABEL_KEYS[step.key]!)}</td>
              <td>
                {step.count === null
                  ? t2('webAdmin.provoz.funnelUmami', { event: step.umamiEvent ?? '' })
                  : String(step.count)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {error !== null ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
    </div>
  );
}
